import { PageHeader, PageShell } from '@/components/PageShell';
import { DomainEventsTable } from '@/app/domainEvents/DomainEventsTable';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCursorPaging } from '@rock/db-admin';
import { domainEventsPageState } from '@/state/domainEvents.state';
import type { FC } from 'react';

const DomainEventsPage: FC = () => {
    const {
        data: events,
        isLoading,
        hasError,
        page,
        totalPages,
        totalCount,
        hasNext,
        hasPrev,
        goNext,
        goPrev,
    } = useCursorPaging(domainEventsPageState, (e) => e.occurredAt);

    return (
        <PageShell>
            <PageHeader
                title="Domain Events"
                description="View the most recent domain events that track changes across the system."
            />
            {isLoading ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Loading events...</CardTitle>
                        <CardDescription>Please wait while we fetch the domain events.</CardDescription>
                    </CardHeader>
                </Card>
            ) : hasError ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Error loading events</CardTitle>
                        <CardDescription>Unable to load events. Please try again later.</CardDescription>
                    </CardHeader>
                </Card>
            ) : events.length === 0 && page === 1 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No events found</CardTitle>
                        <CardDescription>No domain events have been created yet.</CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Domain Events</CardTitle>
                        <CardDescription>
                            Page {page}
                            {totalPages ? ` of ${totalPages}` : ''}
                            {totalCount ? ` (${totalCount.toLocaleString()} total events)` : ''}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <DomainEventsTable events={events} />
                        <div className="mt-4 flex items-center justify-between">
                            <Button variant="outline" disabled={!hasPrev} onClick={goPrev}>
                                Previous
                            </Button>
                            <span className="text-muted-foreground text-sm">
                                Page {page}
                                {totalPages ? ` of ${totalPages}` : ''}
                            </span>
                            <Button variant="outline" disabled={!hasNext} onClick={goNext}>
                                Next
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

export default DomainEventsPage;
